import React, { useState, useMemo, useRef } from 'react';
import { Plus, X, Image, Palette, StickyNote, Trash2, Link, Upload, ChevronDown, Loader2 } from 'lucide-react';
import { weddingThemes } from '../../data/weddingThemes';
import { ImageWithAuth } from '../ImageWithAuth';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const uid = () => `mb-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const ITEM_TYPES = [
  { value: 'image', label: 'Bilde', Icon: Image },
  { value: 'color', label: 'Farge', Icon: Palette },
  { value: 'note', label: 'Notat', Icon: StickyNote },
];

export const MoodboardBuilder = ({ moodboard, onChange, formData }) => {
  const [addType, setAddType] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [noteText, setNoteText] = useState('');
  const [customColor, setCustomColor] = useState('#B49A88');
  const [colorLabel, setColorLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef(null);

  const items = moodboard?.items || [];
  const selectedTheme = useMemo(() =>
    weddingThemes.find(t => t.id === formData?.bookTheme),
    [formData?.bookTheme]
  );

  const updateItems = (newItems) => onChange({ ...(moodboard || {}), items: newItems });

  const addItem = (item) => {
    updateItems([...items, { id: uid(), ...item, createdAt: Date.now() }]);
    setAddType(null);
    setImageUrl('');
    setNoteText('');
    setColorLabel('');
  };

  const removeItem = async (id) => {
    const item = items.find(i => i.id === id);
    // If it has a file_id, delete from storage
    if (item?.file_id) {
      try {
        await axios.delete(`${API}/api/storage/files/${item.file_id}`);
      } catch (err) {
        console.warn('Failed to delete from storage:', err);
      }
    }
    updateItems(items.filter(i => i.id !== id));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    // Validate
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Filen er for stor (maks 10MB)');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setUploadError('Kun bildefiler er tillatt');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      const form = new FormData();
      form.append('file', file);

      const resp = await axios.post(`${API}/api/storage/upload?folder=moodboard`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => {
          if (p.total) setUploadProgress(Math.round((p.loaded / p.total) * 100));
        },
      });

      const { file_id, download_url } = resp.data;
      // Store the file_id; we'll construct the download URL dynamically
      addItem({
        type: 'image',
        content: `${API}${download_url}`,
        label: file.name.split('.')[0],
        file_id,
        storage_path: resp.data.path,
      });
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.response?.data?.detail || 'Opplasting feilet. Try igjen.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const addThemeColor = (color, name) => {
    addItem({ type: 'color', content: color, label: name });
  };

  return (
    <div data-testid="moodboard-builder" className="space-y-5">
      {/* Header */}
      <div>
        <h3 className="font-sans font-semibold text-stone-800 text-sm">Moodboard</h3>
        <p className="text-xs font-sans text-stone-500 mt-0.5">
          Samle inspirasjonsbilder, farger og notes for the wedding
        </p>
      </div>

      {/* Theme color suggestion */}
      {selectedTheme && (
        <div className="bg-white rounded-xl border border-stone-200/80 p-3">
          <p className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-2">
            Farger fra {selectedTheme.name}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedTheme.colors.map((color, i) => {
              const alreadyAdded = items.some(it => it.type === 'color' && it.content === color);
              return (
                <button key={i} onClick={() => !alreadyAdded && addThemeColor(color, selectedTheme.colorNames?.[i] || '')}
                  disabled={alreadyAdded}
                  data-testid={`theme-color-${i}`}
                  className={`group flex items-center gap-1.5 rounded-lg border px-2 py-1.5 transition-all ${
                    alreadyAdded ? 'opacity-40 cursor-not-allowed border-stone-200' : 'border-stone-200 hover:border-primary cursor-pointer hover:shadow-sm'
                  }`}>
                  <div className="w-5 h-5 rounded-full border border-stone-200/50 shadow-inner" style={{ background: color }} />
                  <span className="text-[9px] font-sans font-medium text-stone-600">{selectedTheme.colorNames?.[i] || color}</span>
                  {!alreadyAdded && <Plus className="w-2.5 h-2.5 text-stone-400 group-hover:text-primary" />}
                </button>
              );
            })}
          </div>
          <p className="text-[9px] font-sans text-stone-400 mt-1.5 italic">{selectedTheme.mood}</p>
        </div>
      )}

      {/* Add buttons */}
      <div className="flex items-center gap-2">
        {ITEM_TYPES.map(t => (
          <button key={t.value} data-testid={`add-${t.value}`}
            onClick={() => setAddType(addType === t.value ? null : t.value)}
            className={`flex items-center gap-1.5 text-[10px] font-sans font-semibold px-3 py-1.5 rounded-lg border transition-all ${
              addType === t.value ? 'bg-primary/10 border-primary text-primary' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300'
            }`}>
            <t.Icon className="w-3 h-3" />
            {t.label}
          </button>
        ))}
        <span className="text-[9px] font-sans text-stone-400 ml-auto">{items.length} element{items.length !== 1 ? 'er' : ''}</span>
      </div>

      {/* Add forms */}
      {addType === 'image' && (
        <div className="bg-white rounded-xl border border-stone-200 p-3 space-y-2 animate-fade-in-up" data-testid="add-image-form">
          <div className="flex gap-2">
            <input value={imageUrl} onChange={e => setImageUrl(e.target.value)}
              placeholder="Lim inn bilde-URL..."
              className="flex-1 text-[11px] font-sans px-3 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
            <button onClick={() => { if (imageUrl.trim()) addItem({ type: 'image', content: imageUrl.trim(), label: '' }); }}
              disabled={!imageUrl.trim()}
              className="text-[10px] font-sans font-semibold text-white bg-primary hover:bg-primary/90 px-3 py-1.5 rounded-lg disabled:opacity-40 transition-colors">
              <Link className="w-3 h-3" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-sans text-stone-400">or</span>
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1 text-[10px] font-sans text-primary hover:text-primary/80 transition-colors disabled:opacity-50">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploading ? `Uploading... ${uploadProgress}%` : 'Upload from device'}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
          </div>
          {uploadError && (
            <p className="text-[10px] font-sans text-red-500 mt-1">{uploadError}</p>
          )}
          {uploading && (
            <div className="w-full bg-stone-200 rounded-full h-1 mt-1">
              <div className="bg-primary h-1 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
        </div>
      )}

      {addType === 'color' && (
        <div className="bg-white rounded-xl border border-stone-200 p-3 flex items-center gap-3 animate-fade-in-up" data-testid="add-color-form">
          <input type="color" value={customColor} onChange={e => setCustomColor(e.target.value)}
            className="w-8 h-8 rounded-lg border border-stone-200 cursor-pointer" />
          <input value={colorLabel} onChange={e => setColorLabel(e.target.value)}
            placeholder="Fargenavn (optional)"
            className="flex-1 text-[11px] font-sans px-3 py-1.5 border border-stone-200 rounded-lg focus:border-primary focus:outline-none" />
          <button onClick={() => addItem({ type: 'color', content: customColor, label: colorLabel })}
            className="text-[10px] font-sans font-semibold text-white bg-primary hover:bg-primary/90 px-3 py-1.5 rounded-lg transition-colors">
            Add
          </button>
        </div>
      )}

      {addType === 'note' && (
        <div className="bg-white rounded-xl border border-stone-200 p-3 space-y-2 animate-fade-in-up" data-testid="add-note-form">
          <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
            placeholder="Skriv et notat, idé or inspirasjon..."
            rows={3}
            className="w-full text-[11px] font-sans px-3 py-2 border border-stone-200 rounded-lg focus:border-primary focus:outline-none resize-none" />
          <button onClick={() => { if (noteText.trim()) addItem({ type: 'note', content: noteText.trim(), label: '' }); }}
            disabled={!noteText.trim()}
            className="text-[10px] font-sans font-semibold text-white bg-primary hover:bg-primary/90 px-3 py-1.5 rounded-lg disabled:opacity-40 transition-colors">
            Add notat
          </button>
        </div>
      )}

      {/* Moodboard Grid */}
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-stone-100 flex items-center justify-center mb-3">
            <Palette className="w-6 h-6 text-stone-300" />
          </div>
          <p className="text-sm font-sans font-medium text-stone-400 mb-1">Moodboardet er tomt</p>
          <p className="text-xs font-sans text-stone-300 max-w-xs">
            {selectedTheme ? 'Start by adding colors from your theme above' : 'Add images, colors and notes to build your vision'}
          </p>
        </div>
      ) : (
        <div className="columns-2 sm:columns-3 gap-3 space-y-3" data-testid="moodboard-grid">
          {items.map(item => (
            <div key={item.id} className="break-inside-avoid group relative" data-testid={`moodboard-item-${item.id}`}>
              {item.type === 'image' && (
                <div className="rounded-xl overflow-hidden border border-stone-200/80 bg-stone-50">
                  <ImageWithAuth
                    fileId={item.file_id}
                    src={!item.file_id ? item.content : undefined}
                    alt={item.label || 'Inspirasjon'}
                    className="w-full object-cover"
                    style={{ minHeight: 80 }}
                  />
                  {item.label && (
                    <p className="text-[9px] font-sans text-stone-500 px-2 py-1.5">{item.label}</p>
                  )}
                </div>
              )}
              {item.type === 'color' && (
                <div className="rounded-xl overflow-hidden border border-stone-200/80">
                  <div className="h-20" style={{ background: item.content }} />
                  <div className="bg-white px-2.5 py-1.5 flex items-center justify-between">
                    <span className="text-[9px] font-sans font-medium text-stone-600">{item.label || item.content}</span>
                    <span className="text-[8px] font-sans text-stone-400 font-mono">{item.content}</span>
                  </div>
                </div>
              )}
              {item.type === 'note' && (
                <div className="rounded-xl border border-stone-200/80 bg-[#FFFDE7] p-3">
                  <p className="text-[11px] font-sans text-stone-700 leading-relaxed whitespace-pre-wrap">{item.content}</p>
                </div>
              )}
              <button onClick={() => removeItem(item.id)}
                className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/90 border border-stone-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:border-red-200">
                <X className="w-2.5 h-2.5 text-stone-400 hover:text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
